import { Link } from 'react-router-dom'
import PublicLayout from '../../components/layouts/PublicLayout'
import {
  Shield,
  FileCheck,
  DollarSign,
  Brain,
  Lock,
  Clock,
  CheckCircle,
  ArrowRight,
} from 'lucide-react'

const features = [
  {
    name: 'Audit-Ready Documentation',
    description: 'Locked notes, addendum-only edits, and complete audit trails that survive payer scrutiny.',
    icon: FileCheck,
  },
  {
    name: 'Authorization Governance',
    description: 'Real-time unit tracking prevents billing without valid authorization. Zero tolerance for overages.',
    icon: Shield,
  },
  {
    name: 'Claims Integrity',
    description: 'Claims generated only from locked notes with valid authorization. Scrubber validates before submission.',
    icon: DollarSign,
  },
  {
    name: 'AI Safeguards',
    description: 'AI assists with summarization only. Never fabricates clinical content. Full audit trail of all AI actions.',
    icon: Brain,
  },
  {
    name: 'HIPAA Security',
    description: 'Field-level encryption, immutable audit logs, role-based access, and documented incident response.',
    icon: Lock,
  },
  {
    name: 'Timely Filing Protection',
    description: 'Automated tracking of filing deadlines. Alerts before claims become untimely.',
    icon: Clock,
  },
]

const trustBadges = [
  'HIPAA Compliant',
  'SOC 2 Type II',
  'BAA Available',
  'Azure Hosted',
]

export default function HomePage() {
  return (
    <PublicLayout>
      {/* Hero Section */}
      <div className="relative isolate overflow-hidden bg-gradient-to-b from-primary-50 to-white">
        <div className="mx-auto max-w-7xl px-6 pb-24 pt-16 sm:pb-32 lg:flex lg:px-8 lg:py-32">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl lg:flex-shrink-0">
            {/* Trust badges */}
            <div className="flex flex-wrap gap-2 mb-8">
              {trustBadges.map((badge) => (
                <span
                  key={badge}
                  className="inline-flex items-center rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700"
                >
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {badge}
                </span>
              ))}
            </div>

            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              Audit-Ready ABA Practice Management
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Built for compliance from day one. Every note locks after signature.
              Every claim requires valid authorization. Every AI action is logged.
              Designed to defend against recoupment, not just process claims.
            </p>
            <div className="mt-10 flex items-center gap-x-6">
              <Link to="/contact" className="btn-primary text-base px-6 py-3">
                Request Demo
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <Link to="/features" className="text-sm font-semibold text-gray-900 hover:text-primary-600">
                See all features <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>

          {/* Hero media */}
          <div className="mx-auto mt-16 flex max-w-2xl sm:mt-24 lg:ml-10 lg:mr-0 lg:mt-0 lg:max-w-none lg:flex-none xl:ml-32">
            <div className="max-w-3xl flex-none sm:max-w-5xl lg:max-w-none">
              <div className="overflow-hidden rounded-2xl shadow-2xl ring-1 ring-gray-200 bg-black">
                <video
                  src="/videos/celebrate.mp4"
                  className="w-full h-[320px] object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                  poster="/images/parent1.png"
                />
                <div className="grid grid-cols-3 gap-0 border-t border-gray-200 bg-white">
                  <img src="/images/parent1.png" alt="Family support" className="h-28 w-full object-cover" />
                  <img src="/images/mom.png" alt="Therapist and child" className="h-28 w-full object-cover" />
                  <img src="/images/wheel.png" alt="Inclusive care" className="h-28 w-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Problem/Solution Section */}
      <div className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Stop Living in Fear of Audits
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Most EHRs focus on processing claims. We focus on defending them.
              When the auditor calls, your documentation speaks for itself.
            </p>
          </div>

          <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 lg:max-w-none lg:grid-cols-3">
            <div className="bg-red-50 rounded-lg p-6 border border-red-100">
              <h3 className="text-lg font-semibold text-red-900">The Problem</h3>
              <ul className="mt-4 space-y-2 text-sm text-red-800">
                <li>• Notes edited after billing</li>
                <li>• Sessions billed without authorization</li>
                <li>• Missing supervision documentation</li>
                <li>• AI-generated content without oversight</li>
                <li>• Recoupment demands with no defense</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Typical EHR Response</h3>
              <ul className="mt-4 space-y-2 text-sm text-gray-600">
                <li>• "Add an audit log feature"</li>
                <li>• "Send authorization reminders"</li>
                <li>• "Require supervisor co-sign"</li>
                <li>• "Disable AI features"</li>
                <li>• Compliance as afterthought</li>
              </ul>
            </div>

            <div className="bg-primary-50 rounded-lg p-6 border border-primary-100">
              <h3 className="text-lg font-semibold text-primary-900">Our Approach</h3>
              <ul className="mt-4 space-y-2 text-sm text-primary-800">
                <li>• Notes lock on signature. Period.</li>
                <li>• Claims blocked without valid auth</li>
                <li>• Supervision ratio enforced by system</li>
                <li>• AI permitted for summaries only</li>
                <li>• Compliance is the architecture</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="bg-gray-50 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Compliance Built Into Every Layer
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Not features added later. Enforcement logic that runs before every action.
            </p>
          </div>

          <div className="mx-auto mt-16 max-w-7xl">
            <dl className="grid max-w-xl grid-cols-1 gap-8 lg:max-w-none lg:grid-cols-3">
              {features.map((feature) => (
                <div key={feature.name} className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                  <dt className="flex items-center gap-x-3 text-lg font-semibold text-gray-900">
                    <feature.icon className="h-6 w-6 text-primary-600" />
                    {feature.name}
                  </dt>
                  <dd className="mt-4 text-sm text-gray-600">{feature.description}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary-600">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Ready for Audit-Ready Operations?
            </h2>
            <p className="mt-4 text-lg text-primary-100">
              See how Grace Alone ABA protects your practice from recoupment.
              Schedule a demo with our team.
            </p>
            <div className="mt-8 flex justify-center gap-x-4">
              <Link
                to="/contact"
                className="btn bg-white text-primary-600 hover:bg-primary-50"
              >
                Request Demo
              </Link>
              <Link
                to="/compliance"
                className="btn bg-primary-500 text-white hover:bg-primary-400 border border-primary-400"
              >
                Learn About Compliance
              </Link>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  )
}
